import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, catchError, first, forkJoin, of, take } from 'rxjs';
import { PMPatientListItem } from '../common/models/internal/pm-patient-list-item.model';
import { SortingHeadersEnum } from '../sections/patient-manager-table/enums/sorting-headers-enum';
import { SortingDirectionsEnum } from 'src/common-panel/enums/sorting-directions-enum';
import { PMRequestsService } from './pm-requests.service';
import { PMPatientListItemJSON } from '../common/models/server-response/pm-patient-list-item-json.model';
import { PMPatientADItemJSON } from '../common/models/server-response/pm-patient-ad-item-json.model';
import { PMShopItemJSON } from '../common/models/server-response/pm-shop-item-json.model';
import { ShopFilterStates } from '../sections/patient-manager-table/enums/shop-filter-states';
import { MatDialog } from '@angular/material/dialog';
import { CountryId } from 'src/common-panel/constants/country-configs.constants';

const dataPartSize = 100

@Injectable({
  providedIn: 'root'
})
export class PatientManagerListService
{
  subscription: Subscription = new Subscription()

  currentPatient: PMPatientListItem | undefined

  protected ngUnsubscribe: Subject<void> = new Subject<void>();

  data: PMPatientListItem[] = []
  loadedPatientListSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  isOpenMapDisabledSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true)

  filterByIdEmailOrName: BehaviorSubject<string> = new BehaviorSubject<string>('')
  filterByCountry: BehaviorSubject<CountryId | null> = new BehaviorSubject<CountryId | null>(null)
  clearedFilters: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true)
  needsToRefresh: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  countriesToUpdateLookupTable: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([])
  timestampsZipCodeRelationTooltip: BehaviorSubject<string> = new BehaviorSubject<string>("")
  isGenerateLookupTableLoadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)

  patientsADData: PMPatientADItemJSON[] = []
  shopsData: PMShopItemJSON[] = []
  pendintDeletionUsers: string[] = []

  _loadedShopsSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  _loadedPatientsADSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
  _loadedPendingDeleteSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)


  newPatinetShop:PMShopItemJSON|undefined
  
  tableSettings: {
    sortingHeader: SortingHeadersEnum;
    sortDirection: SortingDirectionsEnum;
    pageSize: number;
    pageIndex: number;
    countryFilterState: CountryId | undefined;
    shopFilterState:ShopFilterStates,
    currentFilterShop?: PMShopItemJSON;
  } = {
      sortingHeader: SortingHeadersEnum.name,
      sortDirection: SortingDirectionsEnum.none,
      pageSize: 20,
      pageIndex: 0,
      countryFilterState: undefined,
      shopFilterState:ShopFilterStates.all
    }


   
    
  constructor(private _dialog: MatDialog,private requestsService: PMRequestsService) { }

  clearAll()
  {
    this.ngUnsubscribe.complete();
    this.subscription.unsubscribe();
    this.data = [];
  }

  loadADPatientsData(country: CountryId)
  {
    this._loadedPatientsADSubject.next(false)
    this.requestsService.getPatientsAD(country).pipe(take(1)).subscribe(l_patientsAD =>
    {
      this._loadedPatientsADSubject.next(true);
      this.patientsADData = l_patientsAD;
    })
  }

  loadPendingDeletionPatients()
  {
    this._loadedShopsSubject.next(false)
    this.requestsService.getPendingDeletionPatients().pipe(take(1)).subscribe(l_pendintDeletionUsers =>
    {
      this.pendintDeletionUsers = l_pendintDeletionUsers;
      this._loadedPendingDeleteSubject.next(true);
    })
  }

  loadShopsData(country: CountryId)
  {
    this._loadedShopsSubject.next(false)
    this.requestsService.getShopsInfo(country).pipe(take(1)).subscribe(l_shopsData =>
    {
      this.shopsData = l_shopsData;
      this._loadedShopsSubject.next(true);
    })
  }

  loadFirstPartData(country: CountryId): BehaviorSubject<boolean>
  {
    this.data = []
    return this.loadPartialData(dataPartSize, 0, country)
  }

  loadRemainingPartData(country: CountryId): BehaviorSubject<boolean>
  {
    return this.loadPartialData(dataPartSize * 1000, dataPartSize, country)
  }

  loadPartialData(limit: number, offset: number, country: CountryId): BehaviorSubject<boolean>
  {

    let l_loadedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
    forkJoin(
      {
        patientsADLoaded: this._loadedPatientsADSubject.pipe(first(val => val == true)),
        shopsDataLoaded: this._loadedShopsSubject.pipe(first(val => val == true)),
        pendingDeleteLoaded: this._loadedPendingDeleteSubject.pipe(first(val => val == true)),
      })
      .subscribe(() =>
      {
        this.requestsService.getPatientList(country, limit, offset).pipe(
          take(1),
          catchError(() => { return of([]) })
        )
          .subscribe(l_patients =>
          {

            let l_data: PMPatientListItem[] = []


            l_patients.forEach((l_patient) =>
            {
              let l_patientAD = this.patientsADData.filter(l_patientADItem => l_patientADItem.id == l_patient.patientId)[0];
              if (l_patientAD) this.checkPatientADData(l_patientAD)
              let l_shopInfo
              if (l_patientAD) l_shopInfo = this.shopsData.filter(l_shop => l_shop.shopId == l_patientAD.shopId)[0];
              this.checkPatientData(l_patient)
              if(l_shopInfo) this.checkShopInfo(l_shopInfo)

              let l_patientListItem: PMPatientListItem = {
                patientId: l_patient.patientId,
                email: l_patientAD ? l_patientAD.email : "–",
                firstName: l_patientAD ? l_patientAD.firstName : "–",
                lastName: l_patientAD ? l_patientAD.lastName : "–",
                 dateOfBirth: l_patientAD ? l_patientAD.dateOfBirth : "–",
                shopId: l_patientAD ? l_patientAD.shopId : "–",
                shopName: l_shopInfo ? l_shopInfo.shopName : "–",
                shopAddress: l_shopInfo ? [l_shopInfo.addressLine1,l_shopInfo.addressLine2].join(", ") : "–",
                dateOfRegistration: l_patient.registrationDate,
                lastSeen: l_patient.lastSeen,
                sunscriptionState: l_patient.subscriptionState,
                isPendingDeletion:this.pendintDeletionUsers.filter(l_user=>l_user==l_patient.patientId)?.length>0,
                authSource:l_patientAD?l_patientAD.authSource:null
              }


              l_data.push(l_patientListItem)


            })
            this.data = this.data.concat(l_data)
            this.loadedPatientListSubject.next(true)
            l_loadedSubject.next(true)

          }
          )
      })
    return l_loadedSubject
  }

  clearPatient()
  {
    this.currentPatient = undefined
  }

  resetPatientPassword(patientId: string):BehaviorSubject<boolean>
  {
    let l_requestOngoing:BehaviorSubject<boolean>=new BehaviorSubject<boolean>(true)
    this.requestsService.resetPatientPassword(patientId).pipe(take(1)).subscribe(() =>
    {
      l_requestOngoing.next(false)
     this._dialog.closeAll();
    })
    return l_requestOngoing
  }

  deletePatient(patientId: string):BehaviorSubject<boolean>
  {
    let l_requestOngoing:BehaviorSubject<boolean>=new BehaviorSubject<boolean>(true)
    this.requestsService.deletePatient(patientId).pipe(take(1)).subscribe(() =>
    {
      l_requestOngoing.next(false);
      setTimeout(() => {
          this.needsToRefresh.next(true)
      }, 200);
    
     this._dialog.closeAll();
    })
    return l_requestOngoing
  }

  transferPatient(patientId: string,shopId:string):BehaviorSubject<boolean>
  {
    let l_requestOngoing:BehaviorSubject<boolean>=new BehaviorSubject<boolean>(true)
    this.requestsService.transferPatientToCLinic(patientId,shopId).pipe(take(1)).subscribe(() =>
    {
      l_requestOngoing.next(false);
      setTimeout(() => {
        this.needsToRefresh.next(true)
    }, 200);
  
     this._dialog.closeAll();
    })
    return l_requestOngoing
  }


  checkPatientData(patientListItemJSON: PMPatientListItemJSON)
  {

    if (!patientListItemJSON.patientId)
    {
      console.error(`Error: Patient ${ JSON.stringify(patientListItemJSON) } hasn't ID.`)
    }


    let l_error: string[] = []

    if (!patientListItemJSON.registrationDate)
    {
      l_error.push(` - hasn't dateOfRegistration.`)
    }

    if (!patientListItemJSON.lastSeen)
    {
      l_error.push(` - hasn't lastSeen.`)
    }

    if (!patientListItemJSON.subscriptionState)
    {
      l_error.push(` - hasn't subscriptionState.`)
    }

    if (l_error.length > 0)
    {
      console.error(` Error in Patient ${ JSON.stringify(patientListItemJSON) }:\n` + l_error.join(';\n'))
    }

  }

  checkPatientADData(patientADItemJSON: PMPatientADItemJSON)
  {

    if (!patientADItemJSON.id)
    {
      console.error(`Error: Patient ${ JSON.stringify(patientADItemJSON) } hasn't ID.`)
    }


    if (!patientADItemJSON.shopId)
    {
      console.error(`Error: Patient ${ JSON.stringify(patientADItemJSON) } hasn't shopId ot shopId is undefined (${ patientADItemJSON.shopId }).`)
    }

    let l_error: string[] = []

    if (!patientADItemJSON.dateOfBirth)
    {
      l_error.push(` - hasn't dateOfBirth.`)
    }

    if (!patientADItemJSON.firstName)
    {
      l_error.push(` - hasn't firstName.`)
    }

    if (!patientADItemJSON.lastName)
    {
      l_error.push(` - hasn't lastName.`)
    }

    if (!patientADItemJSON.email)
    {
      l_error.push(` - hasn't email.`)
    }

    if (l_error.length > 0)
    {
      console.error(` Error in Patient ${ JSON.stringify(patientADItemJSON) }:\n` + l_error.join(';\n'))
    }

  }


  checkShopInfo(shopItemJSON: PMShopItemJSON)
  {

    if (!shopItemJSON.shopId)
    {
      console.error(`Error: Shop ${ JSON.stringify(shopItemJSON) } hasn't ID.`)
    }

    let l_error: string[] = []

    if (!shopItemJSON.shopName)
    {
      l_error.push(` - hasn't shopName.`)
    }
    if (!shopItemJSON.addressLine1)
    {
      l_error.push(` - hasn't addressLine1.`)
    }
    if (!shopItemJSON.addressLine2)
    {
      l_error.push(` - hasn't addressLine2.`)
    }
    if (!shopItemJSON.city)
    {
      l_error.push(` - hasn't city.`)
    }

    if (l_error.length > 0)
    {
      console.error(` Error in Shop ${ JSON.stringify(shopItemJSON) }:\n` + l_error.join(';\n'))
    }

  }



}
